import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { MichiganTitleVehicleApplicationReadyToPrintDto } from 'dtos'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { useState } from 'react'
import {
  michiganTitlesCompactCellSx,
  michiganTitlesCompactFormControlLabelSx,
  michiganTitlesDateCellSx,
  michiganTitlesTableContainerSx,
  michiganTitlesTableSx
} from './michiganTitlesTableStyles'

interface ApplicationReadyToPrintCardProps {
  includeApplicationPacketsReadyForMISoSBundle: boolean
  setIncludeApplicationPacketsReadyForMISoSBundle: React.Dispatch<React.SetStateAction<boolean>>
}

export default function ApplicationReadyToPrintCard({
  includeApplicationPacketsReadyForMISoSBundle,
  setIncludeApplicationPacketsReadyForMISoSBundle
}: ApplicationReadyToPrintCardProps) {
  const { handleSubmit, isSubmitting, setFieldValue, values } =
    useFormikContext<MichiganTitleVehicleApplicationReadyToPrintDto[]>()

  const [isGeneratingApplicationPacket, setIsGeneratingApplicationPacket] = useState<boolean>(false)
  const [selected, setSelected] = useState<MichiganTitleVehicleApplicationReadyToPrintDto[]>([])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item sm={12} md={6}>
            <Typography fontWeight='bold' variant='h6'>
              Application Ready to Print
            </Typography>
          </Grid>

          <Grid item sm={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeApplicationPacketsReadyForMISoSBundle}
                  onChange={() => {
                    setIncludeApplicationPacketsReadyForMISoSBundle(!includeApplicationPacketsReadyForMISoSBundle)
                  }}
                  size='small'
                />
              }
              label='Include Application Packets Ready For MI SoS Bundle'
              sx={michiganTitlesCompactFormControlLabelSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={michiganTitlesTableContainerSx}>
              <Table size='small' sx={michiganTitlesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'></TableCell>

                    <TableCell>Importer Stock #</TableCell>

                    <TableCell>Exporter Company</TableCell>

                    <TableCell>Buyer Company</TableCell>

                    <TableCell>PAPS #</TableCell>

                    <TableCell>Signed BOL</TableCell>

                    <TableCell>Police Book #</TableCell>

                    <TableCell>VIN</TableCell>

                    <TableCell>Year</TableCell>

                    <TableCell>Make</TableCell>

                    <TableCell>Model</TableCell>

                    <TableCell>Application Packet Ready To Print</TableCell>

                    <TableCell>Application Packet Ready For MI SoS Bundle</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {values.map((vehicle, index) => (
                    <TableRow key={vehicle.vehicleGuid}>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selected.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                          size='small'
                          onChange={e => {
                            const isChecked = selected.some(v => v.vehicleGuid === vehicle.vehicleGuid)

                            setSelected(
                              !isChecked ? [...selected, vehicle] : selected.filter(v => v.vehicleGuid !== vehicle.vehicleGuid)
                            )

                            setFieldValue(`[${index}].michiganTitleIsApplicationPacketReadyForMISoSBundle`, !isChecked)
                          }}
                        />
                      </TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.importerStockNumber}</TableCell>

                      <TableCell>{vehicle.exporterCompany}</TableCell>

                      <TableCell>{vehicle.buyerCompany ?? '-'}</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.papsNumber ?? '-'}</TableCell>

                      <TableCell>-</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.michiganTitlePoliceBookNumber}</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.vin}</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.year}</TableCell>

                      <TableCell>{vehicle.make}</TableCell>

                      <TableCell>{vehicle.model}</TableCell>

                      <TableCell sx={michiganTitlesDateCellSx}>
                        {vehicle.michiganTitleApplicationPacketReadyToPrintDateTime
                          ? moment(vehicle.michiganTitleApplicationPacketReadyToPrintDateTime).format('M/D/yyyy')
                          : '-'}
                      </TableCell>

                      <TableCell sx={michiganTitlesDateCellSx}>
                        {vehicle.michiganTitleApplicationPacketReadyForMISoSBundleDateTime
                          ? moment(vehicle.michiganTitleApplicationPacketReadyForMISoSBundleDateTime).format('M/D/yyyy')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <LoadingButton
                color='primary'
                disabled={isSubmitting || selected.length === 0}
                loading={isGeneratingApplicationPacket}
                onClick={() => {
                  setIsGeneratingApplicationPacket(true)
                  controllers.michiganTitles.generateApplicationPacket(selected).finally(() => {
                    setIsGeneratingApplicationPacket(false)
                  })
                }}
                size='small'
                variant='contained'
              >
                GENERATE APPLICATION PACKET FOR SELECTED VEHICLES
              </LoadingButton>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={isSubmitting || selected.length === 0}
                onClick={() => {
                  setSelected([])
                  handleSubmit()
                }}
                size='small'
                variant='contained'
              >
                MARK SELECTED VEHICLES APPLICATION READY FOR MI SOS BUNDLE
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Application Ready to Print Table:**

Vehicles appear in the Application Packet Ready to Print table when the vehicle’s Application Packet Ready To Print column is populated.

**Include Application Packets Ready For MI SoS Bundle Checkbox:**

The Include Application Packets Ready For MI SoS Bundle checkbox is unchecked by default.

Turning this checkbox on includes vehicles with the Application Packet Ready For MI SoS Bundle column populated in the Application Packet Ready to Print table.

**Generate Application Packet For Selected Vehicles Button:**

This button is Enabled after at least one vehicle record is selected.

Clicking this button generates and downloads the Michigan Title Application Packet PDF, containing title documents for the selected vehicles.

**Mark Selected Vehicles’ Application Ready For MI SoS Bundle Button:**

This button is Enabled after at least one vehicle record is selected.

Clicking this button will do the following:
   - Populates the Application Packet Ready For MI SoS Bundle column with the current date.
   - Saves the current date and the user’s name to the Application Packet Ready For MI SoS Bundle field on the Michigan Title Application card (under Vehicle Details – Titles tab).
   - Generates and stores individual PDFs for each selected vehicle under Vehicle Details – Documents tab.

**PDFs Generated:**

Clicking Generate Application Packet For Selected Vehicles produces the following documents for each vehicle:

   - TR-34 Certification: Michigan Department of State Certification.
   - 7501: Added from the existing file generated during the Inbound Trips/Send to Broker process.
   - RD-108 Application: Michigan Department of State Application for Title and Registration of Vehicle Sale.
   - TR-114 Special Mailer: Special Mailing of Certificate of Title.
   - HS-7 Declaration: Added from the existing file generated during the Inbound Trips/Send to Broker process.

When multiple vehicles are included, documents are grouped by VIN in this sequence:

VIN 1:
   - TR-34 Certification
   - 7501
   - RD-108 Application
   - TR-114 Special Mailer
   - HS-7 Declaration

VIN 2:
   - TR-34 Certification
   - 7501
   - RD-108 Application
   - TR-114 Special Mailer
   - HS-7 Declaration`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
